import Paper from '@/components/Paper'
import { DateRangePicker } from '@/components/ui/date-picker'
import type { DateRange, EndDatePreset } from '@/components/ui/date-picker'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useTourHandler } from '@/contexts/tour-handler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import dayjs from 'dayjs'
import 'dayjs/locale/vi'
import isoWeek from 'dayjs/plugin/isoWeek'
import React, { useEffect, useState } from 'react'
import { getWorkdaysReport } from '../../../services'
import ComplianceAssessmentChart from './ComplianceAssessmentChart'
import EmployeeTable from './EmployeeTable'
import MonthlyComparisonChart from './MonthlyComparisonChart'
import OnTimeRateWidget from './OnTimeRateWidget'
import TopEmployeesWidget from './TopEmployeesWidget'
import { useWorkdayDashboardTour } from './use-workday-dashboard-tour'
import { useWorkdayDashboard } from './use-workday-dashboard'
import { DrillState, DrillLevel } from './workday-dashboard-config'
import { StatsCards } from './components/StatsCards'
import { DrilldownChart } from './components/DrilldownChart'

dayjs.extend(isoWeek)
dayjs.locale('vi')

const END_DATE_PRESETS: EndDatePreset[] = [
    {
        name: '+1m',
        label: '+1 tháng',
        getEndDate: (d) => d.add(1, 'month').subtract(1, 'day').toDate(),
    },
    {
        name: '+3m',
        label: '+3 tháng',
        getEndDate: (d) => d.add(3, 'month').subtract(1, 'day').toDate(),
    },
    {
        name: '+6m',
        label: '+6 tháng',
        getEndDate: (d) => d.add(6, 'month').subtract(1, 'day').toDate(),
    },
    {
        name: '+1y',
        label: '+1 năm',
        getEndDate: (d) => d.add(1, 'year').subtract(1, 'day').toDate(),
    },
]

/**
 * WorkDay Dashboard - Main dashboard for workday analytics
 *
 * Features:
 * - Summary statistics cards
 * - Monthly comparison charts
 * - On-time rate widget
 * - Compliance assessment
 * - Drilldown chart (company → department → employee)
 * - Top employees ranking
 * - Employee details table
 */
const Dashboard: React.FC = () => {
    const { startTour } = useWorkdayDashboardTour()
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Date range state
    const [dateRange, setDateRange] = useState<DateRange>(() => ({
        from: dayjs().startOf('month').toDate(),
        to: dayjs().endOf('month').toDate(),
    }))

    // Convert to API format { gte, lte }
    const apiDateRange = {
        gte: dayjs(dateRange.from).format('YYYY-MM-DD'),
        lte: dateRange.to
            ? dayjs(dateRange.to).format('YYYY-MM-DD')
            : dayjs(dateRange.from).format('YYYY-MM-DD'),
    }

    // Drill-down state for chart navigation
    const [drillState, setDrillState] = useState<DrillState>({
        level: 'company',
    })

    // Register tour handler
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    // Fetch workday report data
    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.WORKDAYS_REPORT,
            apiDateRange.gte,
            apiDateRange.lte,
        ],
        queryFn: () => getWorkdaysReport(apiDateRange),
        staleTime: 5 * 60 * 1000,
    })

    // Transform data using business logic hook
    const { stats, chartData } = useWorkdayDashboard(data, drillState)

    // Handle chart bar click for drill-down
    const handleBarClick = (chartItem: any) => {
        if (drillState.level === 'company' && chartItem) {
            setDrillState({
                level: 'department',
                departmentId: chartItem.id,
                departmentName: chartItem.name,
            })
        }
    }

    // Handle breadcrumb navigation
    const handleBreadcrumbClick = (level: DrillLevel) => {
        if (level === 'company') {
            setDrillState({ level: 'company' })
        }
    }

    const stateComponent = renderState()

    return (
        <div className="space-y-2">
            {/* Date Range Selector */}
            <Paper className="py-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-start-4">
                        <DateRangePicker
                            value={dateRange}
                            onChange={(range) => range && setDateRange(range)}
                            showPresets
                            align="end"
                            className="w-full"
                            endDatePresets={END_DATE_PRESETS}
                        />
                    </div>
                </div>
            </Paper>

            {stateComponent ? (
                stateComponent
            ) : (
                <>
                    {/* Summary Statistics Cards */}
                    <StatsCards
                        totalEmployees={stats.totalEmployees}
                        totalWorkDays={stats.totalWorkDays}
                        totalLateDays={stats.totalLateDays}
                        avgAttendance={stats.avgAttendance}
                    />

                    {/* Monthly Comparison & Widgets Row */}
                    <div className="grid grid-cols-12 gap-2 items-stretch">
                        <div className="col-span-9 h-full">
                            <MonthlyComparisonChart dateRange={apiDateRange} />
                        </div>
                        <div className="col-span-3 flex flex-col gap-2">
                            <OnTimeRateWidget dateRange={apiDateRange} />
                            <ComplianceAssessmentChart
                                dateRange={apiDateRange}
                            />
                        </div>
                    </div>

                    {/* Drilldown Chart & Top Employees Row */}
                    <div className="grid grid-cols-3 gap-2 items-stretch">
                        <DrilldownChart
                            chartData={chartData}
                            drillState={drillState}
                            onBarClick={handleBarClick}
                            onBreadcrumbClick={handleBreadcrumbClick}
                        />

                        {/* Top Employees Widget */}
                        <div
                            className="col-span-1 h-full"
                            data-tour="workday-top-employees"
                        >
                            <TopEmployeesWidget dateRange={apiDateRange} />
                        </div>
                    </div>

                    {/* Employee Details Table */}
                    <EmployeeTable data={data || []} />
                </>
            )}
        </div>
    )
}

export default Dashboard
